import { useEffect, useState } from "react";
import { SupplierType } from "../../../../config/enums/SupplierType.enum";
import { getSupplierFilters, resetSupplierFilters, saveSupplierFilters } from "../../../../utils/cacheStorage";

const DEFAULT_DISPLAY_ONLY = "all";
const DEFAULT_ORDER_BY = "createdAtDesc";

export interface SupplierFilterState {
  displayOnly: string;
  supplierTypes: string[];
  orderBy: string;
  setDisplayOnly: (value: string) => void;
  setSupplierTypes: (value: string[]) => void;
  setOrderBy: (value: string) => void;
  reset: () => void;
  apply: () => void;
}

/**
 * Tailwind replacement for the legacy `SupplierFilter`'s state
 * (src/screens/supplier/supplierList/SupplierFilter.tsx) — same three
 * fields, same localStorage persistence via the unchanged
 * `getSupplierFilters`/`saveSupplierFilters`/`resetSupplierFilters` helpers.
 * Mirrors the legacy quirk exactly: on mount it restores a previous
 * selection into this panel's own display state only — it does not call
 * `refetchWithFilters`, so the grid itself still starts from hard defaults
 * until the user re-opens the panel and hits Apply.
 */
export const useSupplierFilterState = (
  refetchWithFilters: (isSupplierAsUser: boolean | undefined, supplierTypes: number[], orderBy: string) => void
): SupplierFilterState => {
  const [displayOnly, setDisplayOnly] = useState<string>(DEFAULT_DISPLAY_ONLY);
  const [supplierTypes, setSupplierTypes] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<string>(DEFAULT_ORDER_BY);

  useEffect(() => {
    const { isSupplierAsUser, supplierTypes: storedTypes, orderBy: storedOrderBy } = getSupplierFilters();
    if (isSupplierAsUser !== DEFAULT_DISPLAY_ONLY || storedTypes.length || storedOrderBy !== DEFAULT_ORDER_BY) {
      setDisplayOnly(isSupplierAsUser);
      setSupplierTypes(storedTypes);
      setOrderBy(storedOrderBy);
    }
  }, []);

  const reset = () => {
    setDisplayOnly(DEFAULT_DISPLAY_ONLY);
    setSupplierTypes([]);
    setOrderBy(DEFAULT_ORDER_BY);
    resetSupplierFilters();
    refetchWithFilters(undefined, [], DEFAULT_ORDER_BY);
  };

  const apply = () => {
    saveSupplierFilters(displayOnly, supplierTypes, orderBy);
    const isSupplierAsUser =
      displayOnly === "user" ? true : displayOnly === "notUser" ? false : undefined;
    refetchWithFilters(
      isSupplierAsUser,
      supplierTypes.map(value => parseInt(value, 10)),
      orderBy
    );
  };

  return { displayOnly, supplierTypes, orderBy, setDisplayOnly, setSupplierTypes, setOrderBy, reset, apply };
};

export const SUPPLIER_TYPE_OPTIONS = [
  { value: String(SupplierType.Internal), label: "In-House" },
  { value: String(SupplierType.External), label: "External" }
] as const;
